import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Check } from "lucide-react";
import { Anchor } from "~/components/Anchor";
import { DefaultFormModal } from "~/components/DefaultForm";
import { Button } from "~/components/ui/button";
import { RWA_URLS } from "~/consts";
import { BillingUserEvents } from "~/modules/analytics/events/billing";

// // // //

const benefits = [
    "For businesses of all sizes",
    "Flexible & scalable pricing",
    "Custom onboarding",
    "Admin purchasing",
];

interface EnterpriseCTAProps {
    showDocsLink?: boolean;
    slim?: boolean;
}

export function EnterpriseCTA({ showDocsLink, slim }: EnterpriseCTAProps) {
    if (slim) {
        return (
            <div className="flex flex-col gap-4 rounded-xl bg-slate-800 px-6 py-4 select-none sm:flex-row sm:items-center sm:justify-between">
                <h3 className="max-w-xl text-base font-light leading-snug text-slate-200">
                    Access the industry-standard data platform for tokenized
                    real-world assets.
                </h3>
                <div className="flex items-center gap-3 shrink-0">
                    <DefaultFormModal
                        title="Contact the team"
                        body="Get in touch about our API, data downloads, asset / network integrations, or anything else you have in mind. We are also available at team@rwa.xyz and usually respond within 24 hours."
                        buttonText="Contact Sales"
                        buttonClassName="bg-card text-slate-800 hover:bg-gray-100 h-9 px-4 rounded-lg font-medium"
                        analyticsLabel={
                            BillingUserEvents.OpenEnterpriseContactModal
                        }
                    />
                    {showDocsLink && (
                        <Button
                            asChild
                            variant="ghost"
                            className="border text-background hover:text-slate-800 hover:bg-background h-9 px-4 rounded-lg font-medium"
                        >
                            <Anchor
                                href={RWA_URLS.docs}
                                openInNewTab
                                analyticsLabel={
                                    BillingUserEvents.EnterpriseCTAViewDocsLinkClick
                                }
                                className="flex items-center gap-2"
                            >
                                <span>View Docs</span>
                                <FontAwesomeIcon
                                    icon={faArrowUpRightFromSquare}
                                    className="w-1.5 h-1.5"
                                />
                            </Anchor>
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-xl bg-slate-800 p-8 md:p-12 select-none">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                <div className="flex-1">
                    <h3 className="text-2xl max-w-xl font-light md:text-3xl text-slate-200 mb-6 leading-tight">
                        Access the industry-standard data platform for tokenized
                        real-world assets.
                    </h3>
                    <div className="flex items-center gap-4">
                        <DefaultFormModal
                            title="Contact the team"
                            body="Get in touch about our API, data downloads, asset / network integrations, or anything else you have in mind. We are also available at team@rwa.xyz and usually respond within 24 hours."
                            buttonText="Contact Sales"
                            buttonClassName="bg-card text-slate-800 hover:bg-gray-100 h-11 px-6 rounded-lg font-medium"
                            analyticsLabel={
                                BillingUserEvents.OpenEnterpriseContactModal
                            }
                        />
                        {showDocsLink && (
                            <Button
                                asChild
                                variant="ghost"
                                className="border text-background hover:text-slate-800 hover:bg-background h-11 px-6 rounded-lg font-medium"
                            >
                                <Anchor
                                    href={RWA_URLS.docs}
                                    openInNewTab
                                    analyticsLabel={
                                        BillingUserEvents.EnterpriseCTAViewDocsLinkClick
                                    }
                                    className="flex items-center gap-2"
                                >
                                    <span>View Docs</span>
                                    <FontAwesomeIcon
                                        icon={faArrowUpRightFromSquare}
                                        className="w-1.5 h-1.5"
                                    />
                                </Anchor>
                            </Button>
                        )}
                    </div>
                </div>
                <div className="shrink-0">
                    <ul className="space-y-4">
                        {benefits.map((benefit, index) => (
                            <li
                                key={index}
                                className="flex items-center gap-3 font-light"
                            >
                                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-600">
                                    <Check className="h-3.5 w-3.5 text-white" />
                                </div>
                                <span className="text-white text-sm md:text-base">
                                    {benefit}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}
